import PropTypes from 'prop-types';
import { cloneElement, isValidElement, useEffect, useMemo, useRef } from 'react';
import { pick } from 'lodash-es';
import { getComponentDisplayName } from '@icp/utils';
import { useChangeLanguage } from '@icp/i18n';
import { useConditionalProperty, useField, useStatus } from '../hooks';
import { useFormDisabled, useFormReadonly } from '../FormRenderCtx';
import { useListFieldPath } from '../listFieldPathCtx';

function FormControl(props) {
  const { children: childrenProp, ...other } = props;

  const [, , languageOptions] = useChangeLanguage();

  const children = isValidElement(childrenProp) ? childrenProp : { props: {} };

  const formDisabled = useFormDisabled();
  const formReadonly = useFormReadonly();

  // 当处于 List 循环体内部时，将字段 id 拼接上 basePath 形成完整路径
  // 例如 basePath="productList.0", id="theName" → resolvedId="productList.0.theName"
  const listFieldPath = useListFieldPath();
  const fieldId = children.props.id;
  const resolvedId = listFieldPath && fieldId ? `${listFieldPath}.${fieldId}` : fieldId;

  const allowI18nValue = children.props.allowI18nValue && languageOptions?.length > 1;

  const { value, setValue, setTouched, setValidatorDelegate, setFocus } = useField({
    id: resolvedId,
  });
  const { status, helpers } = useStatus({
    id: resolvedId,
    helperProp: children.props.helper,
  });

  const disabled = useConditionalProperty(children.props.disabled);
  const readonly = useConditionalProperty(children.props.readonly);

  const component = getComponentDisplayName(children.type);

  const validatorRegisterRef = useRef();
  validatorRegisterRef.current = pick(children.props, [
    'id',
    'title',
    'componentProps',
    'validation',
    'readonly',
    'disabled',
    'forceValidation',
  ]);

  useMemo(() => {
    setValidatorDelegate({
      [Symbol.for('validation')]: {
        ...validatorRegisterRef.current,
        component,
      },
    });
  }, [component, setValidatorDelegate]);

  useEffect(() => {
    return () => {
      setValidatorDelegate(undefined);
    };
  }, [setValidatorDelegate]);

  if (!isValidElement(children)) {
    console.error('Children is not a valid react element', children);
    return null;
  }

  return cloneElement(children, {
    ...other,
    value,
    allowI18nValue,
    status,
    helpers,
    disabled: formDisabled || disabled,
    readonly: formReadonly || readonly,
    onChange: (newValue, language, fromUI) => {
      const res = setValue(newValue, language, fromUI);
      children.props.onChange?.(newValue, language, fromUI);
      return res;
    },
    onTouchChanged: setTouched,
    setValidatorDelegate,
    // fieldApi: (api) => setFieldApi(children.props.id, api),
    // todo: The "onFocus: setFocus" should be only applied to the components that need it.
    componentProps: {
      ...children.props.componentProps,
      // ag-grid 没有 onFocus 属性，会报 warning
      onFocus: component !== 'EditableTable' ? setFocus : undefined,
    },
  });
}

FormControl.propTypes = {
  children: PropTypes.element,
};

// for @icp/utils/getComponentDisplayName, otherwise, in production mode, function name will be compressed.
FormControl.displayName = 'FormControl';

export default FormControl;
